import axios from 'axios';
import { getAuthHeader } from './authService';

const API_URL = `${process.env.REACT_APP_SERVER_URL}/api/quickbooks`;

export type QuickBooksStatus = {
  mock: boolean;
  configured: boolean;
  connected: boolean;
  realm_id: string | null;
  environment: string;
  auto_sync_enabled: boolean;
  connected_at: string | null;
  token_expires_at: string | null;
};

export type QuickBooksSettings = {
  environment: string;
  auto_sync_enabled: boolean;
  realm_id: string | null;
  connected_at: string | null;
  token_expires_at: string | null;
  status: QuickBooksStatus;
  has_refresh_token: boolean;
};

export type QuickBooksSyncLog = {
  id: number;
  entity_type: string;
  entity_id: number;
  user_id: number | null;
  action: string;
  status: string;
  qb_entity_id: string | null;
  error_message: string | null;
  createdAt: string;
  user: { id: number; username: string; email: string } | null;
};

export const getQuickBooksStatus = async () => {
  const response = await axios.get(`${API_URL}/status`, { headers: getAuthHeader() });
  return response.data as QuickBooksStatus;
};

export const getQuickBooksSettings = async () => {
  const response = await axios.get(`${API_URL}/settings`, { headers: getAuthHeader() });
  return response.data as QuickBooksSettings;
};

export const updateQuickBooksSettings = async (data: {
  auto_sync_enabled?: boolean;
  environment?: string;
  realm_id?: string;
  refresh_token?: string;
}) => {
  const response = await axios.put(`${API_URL}/settings`, data, { headers: getAuthHeader() });
  return response.data as QuickBooksStatus;
};

/**
 * Starts the server-side QuickBooks OAuth flow. The server builds the Intuit
 * authorization URL so the client secret is never exposed to the browser.
 */
export const getQuickBooksConnectUrl = async (environment: string) => {
  const response = await axios.post(
    `${API_URL}/connect_quickbooks`,
    { environment },
    { headers: getAuthHeader() }
  );
  return response.data as { authorization_url: string };
};

export const getQuickBooksLogs = async (params?: {
  entity_type?: string;
  entity_id?: number;
  limit?: number;
}) => {
  const response = await axios.get(`${API_URL}/logs`, { headers: getAuthHeader(), params });
  return response.data as QuickBooksSyncLog[];
};

export const getFailedQuickBooksSyncs = async () => {
  const response = await axios.get(`${API_URL}/failed`, { headers: getAuthHeader() });
  return response.data as QuickBooksSyncLog[];
};

export const syncCompanyToQuickBooks = async (companyId: string | number) => {
  const response = await axios.post(`${API_URL}/companys/${companyId}/sync`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const syncInvoiceToQuickBooks = async (invoiceId: string | number) => {
  const response = await axios.post(`${API_URL}/invoices/${invoiceId}/sync`, {}, { headers: getAuthHeader() });
  return response.data;
};
